import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Database, HardDrive, Save, TimerReset } from "lucide-react";
import { useState, type FormEvent } from "react";
import { api, type AdminNode, type NodeSpoolSettings, type Session } from "../api";
import { EmptyState, ErrorNotice, LoadingRows, Status, formatTime } from "../components";

const GIB = 1024 ** 3;

export function NodeStoragePage({ role }: { role: Session["role"] }) {
  const client = useQueryClient();
  const [feedback, setFeedback] = useState<string | null>(null);
  const settings = useQuery({
    queryKey: ["node-spool-settings"],
    queryFn: () => api<NodeSpoolSettings>("/admin/v1/node-spool-settings"),
  });
  const nodes = useQuery({
    queryKey: ["nodes"],
    queryFn: () => api<AdminNode[]>("/admin/v1/nodes"),
    refetchInterval: 10_000,
  });
  const update = useMutation({
    mutationFn: (body: {
      expected_version: number;
      cleanup_interval_seconds: number;
      input_cache_retention_hours: number;
      input_cache_max_bytes: number;
      min_spool_free_bytes: number;
      reason: string;
    }) => api<NodeSpoolSettings>("/admin/v1/node-spool-settings", {
      method: "PUT",
      body: JSON.stringify(body),
    }),
    onSuccess: async (data) => {
      setFeedback(`缓存策略 V${data.version} 已保存，在线节点将在下一次心跳自动应用。`);
      await client.invalidateQueries({ queryKey: ["node-spool-settings"] });
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!settings.data) return;
    setFeedback(null);
    const data = new FormData(event.currentTarget);
    update.mutate({
      expected_version: settings.data.version,
      cleanup_interval_seconds: Number(data.get("cleanup_interval_seconds")),
      input_cache_retention_hours: Number(data.get("input_cache_retention_hours")),
      input_cache_max_bytes: Math.round(Number(data.get("input_cache_max_gib")) * GIB),
      min_spool_free_bytes: Math.round(Number(data.get("min_spool_free_gib")) * GIB),
      reason: String(data.get("reason")),
    });
  };

  const onlineStates = new Set(["READY", "WARMING", "DEGRADED", "DRAINING", "QUARANTINED"]);
  const onlineNodes = nodes.data?.filter((node) => onlineStates.has(node.status)) ?? [];
  const reported = onlineNodes.filter((node) => node.input_cache_bytes != null);
  const totalCache = reported.reduce((sum, node) => sum + (node.input_cache_bytes ?? 0), 0);
  const freeValues = onlineNodes.flatMap((node) => node.spool_free_bytes == null ? [] : [node.spool_free_bytes]);
  const lowestFree = freeValues.length ? Math.min(...freeValues) : null;
  const failedNodes = onlineNodes.filter((node) => (node.last_spool_cleanup_failures ?? 0) > 0).length;

  return <div className="page">
    <header className="page-heading"><div><p className="eyebrow">CAPACITY / CACHE & DISK</p><h1>缓存与磁盘</h1><p>统一下发节点清理策略，并监控缓存占用、磁盘余量与最近一次清理结果。</p></div></header>
    {(settings.error || nodes.error || update.error) && <ErrorNotice error={settings.error ?? nodes.error ?? update.error} />}
    {feedback && <div className="notice notice--positive" role="status"><CheckCircle2 size={17} />{feedback}</div>}

    <section className="spool-overview" aria-label="节点磁盘摘要">
      <div><Database size={17} /><span>当前 RAW 缓存</span><strong>{formatBytes(reported.length ? totalCache : null)}</strong><small>{reported.length} / {onlineNodes.length} 个在线节点已上报</small></div>
      <div><HardDrive size={17} /><span>最低可用空间</span><strong>{formatBytes(lowestFree)}</strong><small>所有在线上报节点中的最低值</small></div>
      <div className={failedNodes ? "spool-overview__warning" : ""}><AlertTriangle size={17} /><span>最近清理异常</span><strong>{failedNodes}</strong><small>最近一次清理存在失败的节点</small></div>
    </section>

    {settings.isPending ? <LoadingRows columns={2} /> : settings.data && <form className="spool-settings" key={settings.data.version} onSubmit={submit}>
      <section className="spool-settings__intro">
        <p className="eyebrow">全局策略 · V{settings.data.version}</p>
        <h2>自动清理策略</h2>
        <p>任务自己的临时 RAW 和 MP3/WAV 会在任务结束时立即删除；这里控制失败重试缓存、周期清理频率和磁盘保护线。</p>
        <time>更新于 {formatTime(settings.data.updated_at)}</time>
      </section>
      <fieldset disabled={role !== "ADMIN" || update.isPending}>
        <div className="spool-settings__fields">
          <label className="field">扫描间隔（秒）<input name="cleanup_interval_seconds" type="number" min="30" max="3600" defaultValue={settings.data.cleanup_interval_seconds} required /><span className="field__hint">默认 60 秒；在线节点通过心跳接收更新。</span></label>
          <label className="field">失败重试缓存保留（小时）<input name="input_cache_retention_hours" type="number" min="1" max="8760" defaultValue={settings.data.input_cache_retention_hours} required /><span className="field__hint">默认 168 小时（7 天），避免重试时重复下载。</span></label>
          <label className="field">单节点缓存上限（GiB）<input name="input_cache_max_gib" type="number" min="0" max="1024" step="0.1" defaultValue={bytesToGiB(settings.data.input_cache_max_bytes)} required /><span className="field__hint">超出后优先删除最早缓存；0 表示不保留重试缓存。</span></label>
          <label className="field">最小磁盘余量（GiB）<input name="min_spool_free_gib" type="number" min="0" max="1024" step="0.1" defaultValue={bytesToGiB(settings.data.min_spool_free_bytes)} required /><span className="field__hint">低于保护线会先清缓存，并暂停领取新任务。</span></label>
          {role === "ADMIN" && <label className="field field--wide">变更原因<input name="reason" minLength={3} maxLength={500} placeholder="例如：扩大节点重试缓存容量" required /></label>}
        </div>
        <div className="spool-settings__actions"><span>{role === "ADMIN" ? "配置作用于全部在线和后续接入节点" : "当前账号只有查看权限"}</span>{role === "ADMIN" && <button className="button button--primary" type="submit" disabled={update.isPending}><Save size={16} />{update.isPending ? "正在保存…" : "保存并下发"}</button>}</div>
      </fieldset>
    </form>}

    <section className="section-block">
      <div className="section-heading"><div><p className="eyebrow">NODE TELEMETRY</p><h2>节点清理状态</h2></div><span className="section-heading__meta">每 10 秒刷新 · 缓存按清理周期采样</span></div>
      {nodes.isPending ? <LoadingRows columns={7} /> : nodes.data?.length ? <div className="table-wrap"><table className="responsive-table spool-table"><thead><tr><th>节点</th><th>状态</th><th>RAW 缓存</th><th>磁盘可用</th><th>最近清理</th><th>本次释放</th><th>结果</th></tr></thead><tbody>{nodes.data.map((node) => <tr key={node.node_id}><td data-label="节点"><strong>{node.name}</strong><small>{node.pool_id}</small></td><td data-label="状态"><Status value={node.status} /></td><td data-label="RAW 缓存">{formatBytes(node.input_cache_bytes)}</td><td data-label="磁盘可用">{formatBytes(node.spool_free_bytes)}</td><td data-label="最近清理">{node.last_spool_cleanup_at ? <><span>{formatTime(node.last_spool_cleanup_at)}</span><small><TimerReset size={12} />周期采样</small></> : <span className="muted">等待新版 Agent 上报</span>}</td><td data-label="本次释放">{node.last_spool_cleanup_files_removed == null ? "—" : <><span>{formatBytes(node.last_spool_cleanup_bytes_removed)} </span><small>{node.last_spool_cleanup_files_removed} 个文件</small></>}</td><td data-label="结果">{node.last_spool_cleanup_failures == null ? "—" : node.last_spool_cleanup_failures > 0 ? <span className="spool-result spool-result--failed">{node.last_spool_cleanup_failures} 个失败</span> : <span className="spool-result">正常</span>}</td></tr>)}</tbody></table></div> : <EmptyState title="暂无节点">节点注册并启动后，缓存与磁盘数据会显示在这里。</EmptyState>}
    </section>
  </div>;
}

function formatBytes(bytes: number | null) {
  if (bytes == null) return "未上报";
  const units = ["B", "KiB", "MiB", "GiB", "TiB"];
  let value = bytes;
  let index = 0;
  while (value >= 1024 && index < units.length - 1) { value /= 1024; index += 1; }
  return `${value.toFixed(index ? 1 : 0)} ${units[index]}`;
}

function bytesToGiB(bytes: number) {
  return Number((bytes / GIB).toFixed(2));
}
